import { useEffect, useMemo, useState } from 'react';
import { GripVertical, Star } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useReorderPlatforms } from '@/hooks/review-platforms/useReorderPlatforms';
import { PLATFORM_CAPABILITIES, type PlatformConfigRecord } from '@/types/review-platform';

interface PlatformPriorityListProps {
  platforms: PlatformConfigRecord[];
  locationId: string | null;
  disabled?: boolean;
}

/**
 * Drag-to-reorder priority list of CONNECTED platforms (spec §8.3). The
 * top-of-list platform is shown first on the customer's 4-5★ rating screen.
 *
 * Optimistically reorders locally; persists via PATCH /reorder on drop.
 */
export function PlatformPriorityList({
  platforms,
  locationId,
  disabled = false,
}: PlatformPriorityListProps) {
  const reorder = useReorderPlatforms(locationId);

  const connected = useMemo(
    () =>
      platforms
        .filter((p) => p.status !== 'DISCONNECTED')
        .slice()
        .sort((a, b) => a.priority - b.priority),
    [platforms],
  );

  const [order, setOrder] = useState<PlatformConfigRecord[]>(connected);
  useEffect(() => {
    setOrder(connected);
  }, [connected]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = order.findIndex((p) => p.id === active.id);
    const newIndex = order.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next);
    reorder.mutate({
      orderedPlatformConfigIds: next.map((p) => p.id),
      ...(locationId ? { locationId } : {}),
    });
  }

  if (order.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
        Connect at least one platform to set its priority.
      </div>
    );
  }

  if (order.length === 1) {
    return (
      <div className="rounded-md border border-border p-3 text-xs text-muted-foreground">
        Connect a second platform to enable drag-to-reorder.
      </div>
    );
  }

  if (disabled) {
    return (
      <ul className="space-y-2">
        {order.map((p, i) => (
          <StaticRow key={p.id} index={i} config={p} />
        ))}
      </ul>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={order.map((p) => p.id)} strategy={verticalListSortingStrategy}>
        <ul className="space-y-2">
          {order.map((p, i) => (
            <SortableRow key={p.id} index={i} config={p} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function StaticRow({ index, config }: { index: number; config: PlatformConfigRecord }) {
  const cap = PLATFORM_CAPABILITIES[config.platform];

  return (
    <li className="flex items-center gap-3 rounded-md border border-border bg-background p-2.5">
      <span className="w-5 text-sm font-medium tabular-nums text-muted-foreground">
        {index + 1}
      </span>
      {index === 0 && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
      <span className="flex-1 truncate text-sm font-medium">{cap.label}</span>
      <span className="text-xs capitalize text-muted-foreground">
        {config.status.toLowerCase().replace('_', ' ')}
      </span>
    </li>
  );
}

function SortableRow({ index, config }: { index: number; config: PlatformConfigRecord }) {
  const cap = PLATFORM_CAPABILITIES[config.platform];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: config.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-md border border-border bg-background p-2.5"
    >
      <button
        type="button"
        className="cursor-grab text-muted-foreground hover:text-foreground"
        {...attributes}
        {...listeners}
        aria-label={`Drag ${cap.label} to reorder`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-sm font-medium tabular-nums text-muted-foreground w-5">
        {index + 1}
      </span>
      {index === 0 && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
      <span className="flex-1 text-sm font-medium truncate">{cap.label}</span>
      <span className="text-xs text-muted-foreground capitalize">
        {config.status.toLowerCase().replace('_', ' ')}
      </span>
    </li>
  );
}
